import { useState, useEffect, useRef } from "react";
import axios from "axios";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

/**
 * CourseCompletionCertificate Component
 * Generates and displays certificates for completed courses
 */
function CourseCompletionCertificate({ user, subjectId, gradeLevel, onClose }) {
  const [certificate, setCertificate] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const certificateRef = useRef(null);
  
  useEffect(() => {
    fetchCertificate();
  }, [subjectId, gradeLevel]);
  
  const fetchCertificate = async () => {
    setLoading(true);
    setError("");
    
    try {
      const token = localStorage.getItem("token");
      const response = await axios.get(
        `${API}/certificates/course/${subjectId}/${gradeLevel}`,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      
      if (response.data.eligible) {
        setCertificate(response.data);
      } else {
        setError(response.data.message || "Certificate not available yet");
      }
    } catch (err) {
      setError(err.response?.data?.detail || "Error loading certificate");
    } finally {
      setLoading(false);
    }
  };
  
  const printCertificate = () => {
    if (certificateRef.current) {
      const printWindow = window.open("", "_blank");
      printWindow.document.write(`
        <html>
          <head>
            <title>Certificate - ${certificate?.subject_name}</title>
            <style>
              body { margin: 0; display: flex; justify-content: center; align-items: center; min-height: 100vh; }
              @media print { body { -webkit-print-color-adjust: exact; } }
            </style>
          </head>
          <body>
            ${certificateRef.current.innerHTML}
          </body>
        </html>
      `);
      printWindow.document.close();
      printWindow.print();
    }
  };
  
  const downloadCertificate = async () => {
    // Convert to image using canvas
    try {
      const html2canvas = (await import("html2canvas")).default;
      const canvas = await html2canvas(certificateRef.current, {
        scale: 2,
        backgroundColor: null
      });
      
      const link = document.createElement("a");
      link.download = `certificate-${certificate?.subject_name}-${gradeLevel}.png`;
      link.href = canvas.toDataURL("image/png");
      link.click();
    } catch (err) {
      // Fallback - open print dialog
      printCertificate();
    }
  };
  
  if (loading) {
    return (
      <div style={{
        position: "fixed",
        top: 0,
        left: 0,
        right: 0,
        bottom: 0,
        background: "rgba(0,0,0,0.9)",
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        zIndex: 10000
      }}>
        <div style={{ textAlign: "center", color: "#fff" }}>
          <div style={{ fontSize: "3rem", marginBottom: "20px" }}>📜</div>
          <p>Generating your certificate...</p>
        </div>
      </div>
    );
  }
  
  if (error) {
    return (
      <div style={{
        position: "fixed",
        top: 0,
        left: 0,
        right: 0,
        bottom: 0,
        background: "rgba(0,0,0,0.9)",
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        zIndex: 10000
      }}>
        <div style={{
          background: "#1a1a2a",
          padding: "40px",
          borderRadius: "20px",
          textAlign: "center",
          maxWidth: "400px"
        }}>
          <div style={{ fontSize: "3rem", marginBottom: "20px" }}>📚</div>
          <h3 style={{ color: "#FFD700", marginBottom: "15px" }}>Certificate Not Ready</h3>
          <p style={{ color: "#aaa", marginBottom: "20px" }}>{error}</p>
          <button
            onClick={onClose}
            style={{
              background: "#333",
              color: "#fff",
              border: "none",
              borderRadius: "20px",
              padding: "10px 25px",
              cursor: "pointer"
            }}
          >
            Close
          </button>
        </div>
      </div>
    );
  }
  
  return (
    <div style={{
      position: "fixed",
      top: 0,
      left: 0,
      right: 0,
      bottom: 0,
      background: "rgba(0,0,0,0.95)",
      display: "flex",
      alignItems: "center",
      justifyContent: "center",
      zIndex: 10000,
      padding: "20px",
      overflowY: "auto"
    }}>
      <div style={{ maxWidth: "900px", width: "100%" }}>
        {/* Certificate */}
        <div
          ref={certificateRef}
          style={{
            background: "linear-gradient(135deg, #FFF8DC 0%, #F5DEB3 50%, #FFD700 100%)",
            border: "15px solid #8B4513",
            borderRadius: "10px",
            padding: "50px",
            textAlign: "center",
            position: "relative",
            boxShadow: "0 10px 40px rgba(0,0,0,0.5)"
          }}
        >
          {/* Corner decorations */}
          <div style={{ position: "absolute", top: "20px", left: "20px", fontSize: "2rem" }}>✡</div>
          <div style={{ position: "absolute", top: "20px", right: "20px", fontSize: "2rem" }}>✡</div>
          <div style={{ position: "absolute", bottom: "20px", left: "20px", fontSize: "2rem" }}>✡</div>
          <div style={{ position: "absolute", bottom: "20px", right: "20px", fontSize: "2rem" }}>✡</div>
          
          {/* Header */}
          <div style={{ marginBottom: "20px" }}>
            <h1 style={{
              fontFamily: "Georgia, serif",
              fontSize: "2.5rem",
              color: "#8B4513",
              margin: "0 0 10px 0",
              textTransform: "uppercase",
              letterSpacing: "5px"
            }}>
              Certificate of Completion
            </h1>
            <div style={{
              width: "200px",
              height: "3px",
              background: "#8B4513",
              margin: "0 auto"
            }} />
          </div>
          
          {/* Body */}
          <p style={{
            fontFamily: "Georgia, serif",
            fontSize: "1.2rem",
            color: "#333",
            marginBottom: "15px"
          }}>
            This certifies that
          </p>
          
          <h2 style={{
            fontFamily: "Georgia, serif",
            fontSize: "2.2rem",
            color: "#000",
            margin: "0 0 15px 0",
            borderBottom: "2px solid #8B4513",
            display: "inline-block",
            padding: "0 30px 10px"
          }}>
            {user?.username || "Student"}
          </h2>
          
          <p style={{
            fontFamily: "Georgia, serif",
            fontSize: "1.2rem",
            color: "#333",
            marginBottom: "15px"
          }}>
            has successfully completed the course
          </p>
          
          <h3 style={{
            fontFamily: "Georgia, serif",
            fontSize: "1.8rem",
            color: "#8B4513",
            margin: "0 0 10px 0"
          }}>
            {certificate?.subject_name}
          </h3>
          
          <p style={{
            fontFamily: "Georgia, serif",
            fontSize: "1.1rem",
            color: "#555",
            marginBottom: "30px"
          }}>
            Grade Level: {gradeLevel}
          </p>
          
          {/* Stats */}
          <div style={{
            display: "flex",
            justifyContent: "center",
            gap: "40px",
            marginBottom: "30px"
          }}>
            <div>
              <div style={{ fontSize: "1.5rem", color: "#8B4513", fontWeight: "bold" }}>
                {certificate?.lessons_completed || 0}
              </div>
              <div style={{ fontSize: "0.9rem", color: "#666" }}>Lessons</div>
            </div>
            <div>
              <div style={{ fontSize: "1.5rem", color: "#8B4513", fontWeight: "bold" }}>
                {certificate?.quizzes_passed || 0}
              </div>
              <div style={{ fontSize: "0.9rem", color: "#666" }}>Quizzes Passed</div>
            </div>
            <div>
              <div style={{ fontSize: "1.5rem", color: "#8B4513", fontWeight: "bold" }}>
                {certificate?.average_score || 0}%
              </div>
              <div style={{ fontSize: "0.9rem", color: "#666" }}>Average Score</div>
            </div>
          </div>
          
          {/* Footer */}
          <div style={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "flex-end",
            marginTop: "40px"
          }}>
            {/* Date */}
            <div style={{ textAlign: "left" }}>
              <div style={{
                borderTop: "2px solid #8B4513",
                paddingTop: "10px",
                width: "200px"
              }}>
                <p style={{ margin: 0, color: "#666", fontSize: "0.9rem" }}>
                  Date of Completion
                </p>
                <p style={{ margin: 0, color: "#333", fontWeight: "bold" }}>
                  {certificate?.completion_date || new Date().toLocaleDateString()}
                </p>
              </div>
            </div>
            
            {/* Seal */}
            <div style={{
              width: "100px",
              height: "100px",
              borderRadius: "50%",
              background: "linear-gradient(135deg, #FFD700 0%, #DAA520 100%)",
              border: "3px solid #8B4513",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              flexDirection: "column"
            }}>
              <div style={{ fontSize: "1.5rem" }}>✡</div>
              <div style={{ fontSize: "0.6rem", color: "#8B4513", fontWeight: "bold" }}>YLC</div>
            </div>
            
            {/* Signature */}
            <div style={{ textAlign: "right" }}>
              <div style={{
                borderTop: "2px solid #8B4513",
                paddingTop: "10px",
                width: "200px"
              }}>
                <p style={{ margin: 0, fontFamily: "cursive", fontSize: "1.2rem", color: "#333" }}>
                  Ykbn Yoonw
                </p>
                <p style={{ margin: 0, color: "#666", fontSize: "0.9rem" }}>
                  Chief Priest
                </p>
              </div>
            </div>
          </div>
          
          {/* Institution */}
          <div style={{ marginTop: "30px" }}>
            <p style={{
              fontFamily: "Georgia, serif",
              fontSize: "1rem",
              color: "#8B4513",
              margin: 0
            }}>
              Yisrael Learning Center
            </p>
            <p style={{
              fontFamily: "Georgia, serif",
              fontSize: "0.8rem",
              color: "#666",
              margin: "5px 0 0 0"
            }}>
              Twelve Tribes of Yisrael • Est. 2012
            </p>
          </div>
        </div>
        
        {/* Actions */}
        <div style={{
          display: "flex",
          justifyContent: "center",
          gap: "15px",
          marginTop: "30px"
        }}>
          <button
            onClick={downloadCertificate}
            style={{
              background: "linear-gradient(135deg, #FFD700 0%, #FFA500 100%)",
              color: "#000",
              border: "none",
              borderRadius: "25px",
              padding: "12px 30px",
              cursor: "pointer",
              fontWeight: "bold",
              fontSize: "1rem"
            }}
          >
            📥 Download Certificate
          </button>
          <button
            onClick={printCertificate}
            style={{
              background: "#333",
              color: "#fff",
              border: "2px solid #FFD700",
              borderRadius: "25px",
              padding: "12px 30px",
              cursor: "pointer",
              fontWeight: "bold",
              fontSize: "1rem"
            }}
          >
            🖨️ Print
          </button>
          <button
            onClick={onClose}
            style={{
              background: "#222",
              color: "#aaa",
              border: "none",
              borderRadius: "25px",
              padding: "12px 30px",
              cursor: "pointer",
              fontSize: "1rem"
            }}
          >
            Close
          </button>
        </div>
      </div>
    </div>
  );
}

export default CourseCompletionCertificate;
